import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { needsChoices } from '@carbonwebmail/domain'
import { useState } from 'react'
import { Alert } from './Alert.js'
import { Dialog } from './Dialog.js'
import { Field, SelectField, TextAreaField } from './Field.js'
import { FIELD_KINDS } from './fieldKinds.js'
import type { EditableColumn } from './useDeclaredColumns.js'

/**
 * Renaming a column, and changing what a declared one holds.
 *
 * One dialog for both because they are the same form with a different number of
 * fields in it: a base column has a name and nothing else this can change, and
 * a declared field has a name, a kind and — when the kind is one-of-a-list — the
 * list. Two dialogs would be two places for the name to be validated
 * differently.
 *
 * **Renaming cannot change who a campaign reaches.** A segment rule and a merge
 * token both name the field's *key*, which nothing here touches. The note says
 * so, because "will this break my segments" is the question somebody asks
 * before pressing Save and the honest answer is no.
 *
 * **Changing the kind does not convert what is stored.** Values are text
 * whatever the declaration says, so the kind decides how the board draws the
 * column and how a form asks for it. A value that no longer fits is left as it
 * is rather than cleared — losing a column of data to a mis-click has no undo.
 */
export function ColumnDialog({
  column,
  field,
  onSave,
  onClose,
}: {
  readonly column: EditableColumn
  /** The declaration behind it, for a `field` column. Absent for a base one. */
  readonly field: ContactFieldDto | undefined
  readonly onSave: (change: {
    readonly label: string
    readonly type: ContactFieldDto['type']
    readonly choices: readonly string[]
  }) => Promise<string | undefined>
  readonly onClose: () => void
}) {
  const [label, setLabel] = useState(column.label)
  const [type, setType] = useState<ContactFieldDto['type']>(field?.type ?? 'text')
  const [choices, setChoices] = useState((field?.choices ?? []).join('\n'))
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    setProblem(undefined)
    const refused = await onSave({
      label: label.trim(),
      type,
      // One per line, blanks dropped: a trailing newline is how a list is typed,
      // not an option called "".
      choices: choices
        .split('\n')
        .map((one) => one.trim())
        .filter((one) => one !== ''),
    })
    if (refused === undefined) {
      onClose()
      return
    }
    setProblem(refused)
    setSaving(false)
  }

  return (
    <Dialog
      description={
        column.kind === 'base'
          ? 'One of the columns every board has. Its name is yours; what it holds is not.'
          : undefined
      }
      footer={
        <>
          <button
            className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
            disabled={saving || label.trim() === ''}
            form="column"
            type="submit"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            className="font-medium text-[13px] text-t3 hover:text-t1"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
        </>
      }
      heading={column.kind === 'base' ? 'Rename column' : 'Edit column'}
      onClose={onClose}
    >
      {problem === undefined ? null : <Alert>{problem}</Alert>}

      <form
        id="column"
        onSubmit={(event) => {
          event.preventDefault()
          void save()
        }}
      >
        <Field
          disabled={saving}
          id="column-label"
          label="Name"
          onChange={setLabel}
          type="text"
          value={label}
        />

        {column.kind === 'base' ? null : (
          <>
            <SelectField
              disabled={saving}
              hint="How the board draws it and how a form asks for it. What is already stored is not converted."
              id="column-type"
              label="Type"
              onChange={(value) => setType(value as ContactFieldDto['type'])}
              options={FIELD_KINDS}
              value={type}
            />

            {needsChoices(type) ? (
              <TextAreaField
                disabled={saving}
                hint="One per line, in the order they are offered. Importing adds any it finds that are not here."
                id="column-choices"
                label="Options"
                onChange={setChoices}
                rows={5}
                value={choices}
              />
            ) : null}
          </>
        )}
      </form>

      {column.kind === 'field' ? (
        <p className="mt-5 border-line border-t pt-4 text-[12px] text-t3">
          Renaming is safe: saved segments and <code>{`{{${column.key ?? ''}}}`}</code> in a message
          both name the column's key, which does not change.
        </p>
      ) : null}
    </Dialog>
  )
}
